import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNostrPublish } from "./useNostrPublish";
import { SCORE_KIND, SCORE_TAG } from "@/game/leaderboard";
import type { GameState } from "@/game/types";
import { computeScore, averageHealth } from "@/game/engine";
import { formatSats } from "@/game/format";
import { DIFFICULTIES } from "@/game/data";

export interface PublishScoreArgs {
  state: GameState;
  /** Also publish a public kind-1 note bragging about the run. */
  alsoPostNote: boolean;
}

function bragText(state: GameState): string {
  const victory = state.phase === "victory";
  const survivors = state.party.filter((p) => p.alive).length;
  const total = state.party.length;
  const score = computeScore(state);

  const diff = DIFFICULTIES[state.difficulty];

  if (victory) {
    return (
      `⚡🛒 I reached Freedom City on the BCH Trail!\n\n` +
      `Difficulty: ${diff.emoji} ${diff.label}\n` +
      `Days on trail: ${state.day}\n` +
      `Sats banked: ${formatSats(state.inventory.sats)}\n` +
      `Pioneers survived: ${survivors}/${total}\n` +
      `Final score: ${formatSats(score)}\n\n` +
      `Can you make it across the frontier on peer-to-peer electronic cash? #bchtrail`
    );
  }
  return (
    `🪦 The BCH Trail claimed my wagon after ${state.day} days on ${diff.emoji} ${diff.label}...\n\n` +
    `Final score: ${formatSats(score)}\n\n` +
    `Think you can do better? #bchtrail`
  );
}

export function usePublishScore() {
  const { mutateAsync: publish } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ state, alsoPostNote }: PublishScoreArgs) => {
      const victory = state.phase === "victory";
      const survivors = state.party.filter((p) => p.alive).length;
      const score = computeScore(state);

      const scoreEvent = await publish({
        kind: SCORE_KIND,
        content: bragText(state).split("\n")[0], // short brag on the score event itself
        tags: [
          ["t", SCORE_TAG],
          ["score", String(score)],
          ["outcome", victory ? "victory" : "defeat"],
          ["days", String(state.day)],
          ["sats", String(state.inventory.sats)],
          ["survivors", String(survivors)],
          ["party", String(state.party.length)],
          ["health", String(averageHealth(state.party))],
          ["difficulty", state.difficulty],
          ["alt", "A BCH Trail leaderboard score"],
        ],
      });

      if (alsoPostNote) {
        await publish({
          kind: 1,
          content: bragText(state),
          tags: [
            ["t", "bchtrail"],
            // reference the score event
            ["e", scoreEvent.id],
          ],
        });
      }

      return scoreEvent;
    },
    onSuccess: () => {
      // refresh the leaderboard so the new score shows up
      queryClient.invalidateQueries({ queryKey: ["bch-trail", "leaderboard"] });
    },
  });
}
